import React from 'react';
import { useTranslation } from 'react-i18next';

// Nominal weight of one roll (or one meter) in kg: g/m² × width (m) [× roll length (m)] / 1000.
// The roll length is Roll Length (m), or the legacy length of imported items (meters, as in 0.05X630X800).
// Same formula as the warehouse location utilization (backend warehouse_locations KG_PER_UNIT).
export const nominalWeightKg = (data, unitOfMeasure) => {
  const gsm = parseFloat(data.weight_per_sqm);
  const widthM = parseFloat(data.width) / 1000;
  const unit = (unitOfMeasure || '').toUpperCase();
  if (!(gsm > 0) || !(widthM > 0)) return null;
  if (unit === 'M') return gsm * widthM / 1000;
  if (unit !== 'ROLL') return null;
  const rollLength = parseFloat(data.roll_length_m) || parseFloat(data.length);
  return rollLength > 0 ? gsm * widthM * rollLength / 1000 : null;
};

const RawPropertiesForm = ({ editData, onFieldChange, disabled = false, unitOfMeasure }) => {
  const { t } = useTranslation('warehouse');
  const unit = (unitOfMeasure || '').toUpperCase();
  const nominalWeight = nominalWeightKg(editData, unit);

  const handleFieldChange = (field, value) => {
    onFieldChange(field, value);
  };

  return (
    <div className="form-section">
      <h3>{t('rawPropertiesForm.title')}</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>{t('rawPropertiesForm.grade')}</label>
          <input
            type="text"
            value={editData.grade || ''}
            onChange={(e) => handleFieldChange('grade', e.target.value)}
            disabled={disabled}
            placeholder={t('rawPropertiesForm.gradePlaceholder')}
            maxLength="50"
          />
        </div>

        <div className="form-group">
          <label>{t('rawPropertiesForm.thickness')}</label>
          <input
            type="number"
            step="0.001"
            value={editData.thickness || ''}
            onChange={(e) => handleFieldChange('thickness', parseFloat(e.target.value) || null)}
            disabled={disabled}
            placeholder={t('rawPropertiesForm.thicknessPlaceholder')}
          />
        </div>

        <div className="form-group">
          <label>{t('rawPropertiesForm.width')}</label>
          <input
            type="number"
            step="0.01"
            value={editData.width || ''}
            onChange={(e) => handleFieldChange('width', parseFloat(e.target.value) || null)}
            disabled={disabled}
            placeholder={t('rawPropertiesForm.widthPlaceholder')}
          />
        </div>

        {unit === 'ROLL' ? (
          <div className="form-group">
            <label>{t('rawPropertiesForm.rollLength')}</label>
            <input
              type="number"
              step="0.01"
              value={editData.roll_length_m || editData.length || ''}
              onChange={(e) => handleFieldChange('roll_length_m', parseFloat(e.target.value) || null)}
              disabled={disabled}
              placeholder={t('rawPropertiesForm.rollLengthPlaceholder')}
            />
          </div>
        ) : (
          <div className="form-group">
            <label>{t('rawPropertiesForm.length')}</label>
            <input
              type="number"
              step="0.01"
              value={editData.length || ''}
              onChange={(e) => handleFieldChange('length', parseFloat(e.target.value) || null)}
              disabled={disabled}
              placeholder={t('rawPropertiesForm.lengthPlaceholder')}
            />
          </div>
        )}

        <div className="form-group">
          <label>{t('rawPropertiesForm.weightPerSqm')}</label>
          <input
            type="number"
            step="0.001"
            value={editData.weight_per_sqm || ''}
            onChange={(e) => handleFieldChange('weight_per_sqm', parseFloat(e.target.value) || null)}
            disabled={disabled}
            placeholder={t('rawPropertiesForm.weightPerSqmPlaceholder')}
          />
        </div>

        {(unit === 'ROLL' || unit === 'M') && (
          <div className="form-group">
            <label>{unit === 'ROLL' ? t('rawPropertiesForm.nominalWeightRoll') : t('rawPropertiesForm.nominalWeightMeter')}</label>
            <input
              type="text"
              value={nominalWeight == null ? '' : Number(nominalWeight.toFixed(3)).toLocaleString()}
              disabled
              placeholder={t('rawPropertiesForm.nominalWeightPlaceholder')}
            />
          </div>
        )}

        <div className="form-group">
          <label>{t('rawPropertiesForm.tensileStrength')}</label>
          <input
            type="number"
            step="0.01"
            value={editData.tensile_strength || ''}
            onChange={(e) => handleFieldChange('tensile_strength', parseFloat(e.target.value) || null)}
            disabled={disabled}
            placeholder={t('rawPropertiesForm.tensileStrengthPlaceholder')}
          />
        </div>

        <div className="form-group">
          <label>{t('rawPropertiesForm.colorFastness')}</label>
          <input
            type="text"
            value={editData.color_fastness || ''}
            onChange={(e) => handleFieldChange('color_fastness', e.target.value)}
            disabled={disabled}
            placeholder={t('rawPropertiesForm.colorFastnessPlaceholder')}
            maxLength="50"
          />
        </div>

        <div className="form-group">
          <label>{t('rawPropertiesForm.fireRating')}</label>
          <input
            type="text"
            value={editData.fire_rating || ''}
            onChange={(e) => handleFieldChange('fire_rating', e.target.value)}
            disabled={disabled}
            placeholder={t('rawPropertiesForm.fireRatingPlaceholder')}
            maxLength="50"
          />
        </div>
      </div>
    </div>
  );
};

export default RawPropertiesForm;